import { createReducer, on } from '@ngrx/store';
import { Chat, ChatMessage, ChatSettings, ChatThreads, LoadingStatus } from '@qvs/core';
import * as fromChatActions from './chat.actions';
import { backendAvailableOrHasChanged } from '~/modules/app/slice';
import { UpdateUserPresenceRequest } from '@chat-api';
import { merge } from 'lodash-es';
import { setCurrentClient } from '@qvs/core/user';

export const chatFeatureKey = 'chat';

export interface State {
  chat: Chat;
  settings: ChatSettings;
  chatIsLoading: boolean;
  chatHasErrors: boolean;
  sendMessageIsLoading: boolean;
  sendMessageHasErrors: boolean;
  outOfOfficeMessageIsLoading: boolean;
  outOfOfficeMessageHasErrors: boolean;
  notReadMessages: ChatMessage[];
  threadsLoadingStatus: LoadingStatus;
  threads: ChatThreads;
}

export const initialState: State = {
  chat: {
    messages: [],
    intendedUserIds: [],
    authorId: null
  },
  settings: {
    outOfOfficeMessage: '',
    outOfOfficeMessageEnabled: false
  },
  chatIsLoading: false,
  chatHasErrors: false,
  sendMessageIsLoading: false,
  sendMessageHasErrors: false,
  outOfOfficeMessageHasErrors: false,
  outOfOfficeMessageIsLoading: false,
  notReadMessages: [],
  threadsLoadingStatus: LoadingStatus.IDLE,
  threads: {}
};

export const reducer = createReducer(
  initialState,
  on(backendAvailableOrHasChanged, (state) => ({ ...state, settings: state.settings, chat: { ...state.chat } })),
  on(fromChatActions.loadChat, (state) => ({
    ...state,
    chatIsLoading: true,
    chatHasErrors: false,
    chat: {
      ...state.chat,
      messages: []
    }
  })),
  on(fromChatActions.setChatLoadingStatus, (state, action) => ({
    ...state,
    chatIsLoading: action.isLoading,
    chatHasErrors: action.hasErrors
  })),
  on(fromChatActions.loadOutOfOfficeMessage, (state) => ({
    ...state,
    settings: {
      ...state.settings,
      outOfOfficeMessage: '',
      outOfOfficeMessageEnabled: false
    }
  })),
  on(fromChatActions.loadOutOfOfficeMessageSuccess, (state, action) => ({
    ...state,
    settings: {
      ...state.settings,
      outOfOfficeMessage: action.message,
      outOfOfficeMessageEnabled: action.enabled
    }
  })),
  on(fromChatActions.loadChatFailure, (state, { authorId, intendedUserIds }) => ({
    ...state,
    chat: {
      ...state.chat,
      intendedUserIds,
      authorId
    },
    chatIsLoading: false,
    chatHasErrors: true
  })),
  on(setCurrentClient, (state, action) => ({
    ...state,
    chat: {
      ...state.chat,
      intendedUserIds: [action.client.staffId]
    }
  })),
  on(fromChatActions.loadChatSuccess, (state, { chat }) => {
    const existingBase64Images = {};
    state.chat.messages
      .filter((message) => !!message.imageBase64)
      .forEach((message) => {
        existingBase64Images[message.id] = message.imageBase64;
      });
    const messages = chat.messages
      ? [...chat.messages.map((message) => ({ ...message, imageBase64: existingBase64Images[message.id] }))]
      : [];
    if (state.chat.messages) {
      state.chat.messages.forEach((message) => {
        if (!messages.find((msg) => msg && message && msg.id === message.id)) {
          messages.push(message as any);
        }
      });
    }

    return {
      ...state,
      chatIsLoading: false,
      chatHasErrors: false,
      chat: {
        ...state.chat,
        intendedUserIds: chat.intendedUserIds,
        authorId: chat.authorId,
        messages,
        outOfOfficeMessage: chat.outOfOfficeMessage
      }
    };
  }),
  on(fromChatActions.updateOutOfOfficeMessageFailure, (state) => ({
    ...state,
    settings: {
      ...state.settings
    },
    outOfOfficeMessageIsLoading: false,
    outOfOfficeMessageHasErrors: true
  })),
  on(fromChatActions.updateOutOfOfficeMessageSuccess, (state) => ({
    ...state,
    outOfOfficeMessageIsLoading: false,
    outOfOfficeMessageHasErrors: false
  })),
  on(fromChatActions.sendChatTextMessage, (state) => ({
    ...state,
    sendMessageIsLoading: true,
    sendMessageHasErrors: false
  })),
  on(fromChatActions.sendOrReceiveChatMessageSuccess, (state, { message }) => {
    const messages = state.chat.messages ? state.chat.messages.filter((msg) => msg.id !== message.id) : [];
    messages.push(message);
    return {
      ...state,
      chatIsLoading: false,
      chatHasErrors: false,
      chat: {
        ...state.chat,
        messages
      }
    };
  }),
  on(fromChatActions.loadChatMessagesReceivedSuccess, (state, { messages }) => {
    const existingMessages = state.chat.messages
      ? state.chat.messages.filter((msg) => !messages.some((newMessage) => newMessage.id === msg.id))
      : [];
    messages.forEach((newMessage) => existingMessages.push(newMessage));
    return {
      ...state,
      chat: {
        ...state.chat,
        messages: existingMessages
      }
    };
  }),
  on(fromChatActions.loadChatMessageImageSuccess, (state, { message, base64, url }) => {
    const messages = state.chat.messages ? state.chat.messages.filter((msg) => msg.id !== message.id) : [];
    messages.push({ ...message, imageBase64: base64, imageUrl: url });
    return {
      ...state,
      chat: {
        ...state.chat,
        messages
      }
    };
  }),
  on(fromChatActions.sendChatMessageFailure, (state) => ({
    ...state,
    sendMessageIsLoading: false,
    sendMessageHasErrors: true
  })),
  on(fromChatActions.sendChatMessageFailSizeFailure, (state) => ({
    ...state,
    sendMessageIsLoading: false,
    sendMessageHasErrors: true
  })),
  on(fromChatActions.addChatMessageToNotRead, (state, { message }) => {
    const notReadMessages = state.notReadMessages ? state.notReadMessages.filter((msg) => msg.id !== message.id) : [];
    notReadMessages.push(message);
    return {
      ...state,
      notReadMessages
    };
  }),
  on(fromChatActions.removeAllChatMessagesFromNotRead, (state, { authorId }) => {
    const threads = merge({}, state.threads, {});
    if (threads[authorId] !== undefined) {
      threads[authorId].unreadMessageCount = 0;
    }
    return {
      ...state,
      threads,
      notReadMessages: state.notReadMessages.filter((message) => message.authorId !== authorId)
    };
  }),
  on(fromChatActions.loadUserPresenceStatusSuccess, (state, presence) => ({
    ...state,
    chat: {
      ...state.chat,
      outOfOfficeMessage: presence.status === UpdateUserPresenceRequest.StatusEnum.OFF_LINE ? presence.desc : null
    }
  })),
  on(fromChatActions.loadUserPresenceStatusFailure, (state) => ({
    ...state,
    chat: {
      ...state.chat,
      outOfOfficeMessage: null
    }
  })),
  on(fromChatActions.loadChatThreads, (state) => ({
    ...state,
    threadsLoadingStatus: LoadingStatus.LOADING
  })),
  on(fromChatActions.loadChatThreadsSuccess, (state, { threads }) => ({
    ...state,
    threadsLoadingStatus: LoadingStatus.SUCCEEDED,
    threads,
    notReadMessages: []
  })),
  on(fromChatActions.loadChatThreadsFailure, (state) => ({
    ...state,
    threadsLoadingStatus: LoadingStatus.FAILED
  }))
);
